import { useMemo } from "react";
import {
  UIOrder,
  UIPrice,
  UIPriceBreakdown,
  UIUnifiedPriceBreakdown,
  UIUnifiedPriceBreakdownItem
} from "@flights/types";
import { useI18n } from "@bookingcom/lingojs-react";
import {
  isFeatureRunningClientSide,
  localizationUtils,
  objectUtils,
  priceBreakdownUtils,
  ryanairUtils
} from "@flights/web-api-utils-universal";
import { transformDisclaimersForPriceBreakdown } from "./transforms/disclaimers";
import uniq from "lodash/uniq";
import { getAddOnOrder } from "store/order/selectors";
import { useSelector } from "react-redux";
import usePointOfSale from "hooks/usePointOfSale";
import { BreakdownProps } from "./Breakdown/Breakdown";
import useRouteName from "hooks/useRouteName";
import useEvents from "hooks/useEvents";

export type UnifiedPriceBreakdownPostBookingPaymentControllerProps = {
  order?: UIOrder;
};

type CopyFuncParams = Parameters<typeof priceBreakdownUtils.getExtraCopy>;
type POSType = CopyFuncParams[3]["pointOfSale"];

export function useUnifiedPriceBreakdownPostBookingPaymentController(
  props: UnifiedPriceBreakdownPostBookingPaymentControllerProps
) {
  const { order } = props;
  const i18n = useI18n();
  const ipCountry = usePointOfSale();
  const routeName = useRouteName();
  const { squeak } = useEvents();
  const addOnOrder = useSelector(getAddOnOrder);
  const segments = useMemo(() => order?.airOrder?.flightSegments, [order?.airOrder?.flightSegments]);

  const isHungaryDepartingRyanAir = useMemo(
    () => localizationUtils.isHungaryDepartingRyanAirFlight(segments),
    [segments]
  );

  const baseOptions = useMemo(
    () => ({
      pointOfSale: (ipCountry || "") as POSType,
      isFlightFromRyanAirHungary: isHungaryDepartingRyanAir,
      segments: segments || [],
      shouldKeepZeroPriceItems: true
    }),
    [ipCountry, isHungaryDepartingRyanAir, segments]
  );

  const addedItems: UIUnifiedPriceBreakdownItem[] = useMemo(() => {
    return priceBreakdownUtils.transformUnifiedPriceBreakdownItemForExtra(
      i18n,
      addOnOrder?.ancillaries || {},
      baseOptions
    );
  }, [i18n, addOnOrder, baseOptions]);

  const unifiedPriceBreakdown: UIUnifiedPriceBreakdown | undefined = useMemo(() => {
    const upb = order?.unifiedPriceBreakdown;
    if (isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_PRODUCT_ID_SQUEAK") && upb && !upb.id) {
      squeak(
        `UnifiedPriceTag::useUnifiedPriceBreakdownPostBookingPaymentController - product id is missing - route "${routeName}"`
      );
    }

    if (addedItems.length <= 0) {
      return;
    }

    const currencyCode = addedItems[0].price.currencyCode;
    const zeroPrice: UIPrice = { currencyCode, units: 0, nanos: 0 };

    return {
      price: zeroPrice,
      items: [],
      addedItems
    };
  }, [addedItems, order?.unifiedPriceBreakdown, routeName, squeak]);

  const disclaimers = useMemo(() => {
    const ancillaries = addOnOrder?.ancillaries || {};
    const ancillaryPriceBreakdowns = objectUtils.getObjectKeys(ancillaries).reduce<UIPriceBreakdown[]>((acc, key) => {
      if (ancillaries[key]) {
        if (key === "specialBag") {
          acc.push(...ancillaries[key].map((specialBag) => specialBag.price));
        } else {
          acc.push(ancillaries[key]?.price);
        }
      }
      return acc;
    }, []);

    return uniq(
      ancillaryPriceBreakdowns.flatMap((priceBreakdown) => transformDisclaimersForPriceBreakdown(priceBreakdown))
    );
  }, [addOnOrder?.ancillaries]);

  const isRyanair = useMemo(() => ryanairUtils.isAnyRyanairSegments(segments), [segments]);

  const expandedByDefault = useMemo<BreakdownProps["expandedByDefault"]>(() => {
    return isRyanair
      ? {
          FLIGHT_ADULT: true,
          FLIGHT_CHILD: true,
          FLIGHT_INFANT: true,
          EXTRA: true
        }
      : undefined;
  }, [isRyanair]);

  return {
    unifiedPriceBreakdown,
    expandedByDefault,
    isHungaryDepartingRyanAir,
    disclaimers,
    addedItems
  };
}
